import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast';
import { Sparkles, Upload, FileText, ShieldCheck, Check, X as XIcon, ArrowRight, ArrowLeft, Euro } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const TOTAL_STEPS = 4;

/**
 * 4-step onboarding shown on first admin login when practice.wizard_completed
 * is not yet true. Renders as a full-screen overlay above the dashboard.
 * After step 4 marks the wizard complete, fires confetti and navigates to /heute.
 */
export default function OnboardingWizard() {
  const { t } = useTranslation();
  const { user, practice, setPractice } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Defense in depth: the wizard MUST only run against the admin's OWN
  // practice. DashboardLayout already gates this, but in case anyone
  // renders us directly we refuse to operate on a branch context — the
  // /api/practices/:id PATCH would 403 anyway (canAccessPractice).
  const ownPracticeId = user?.practice_id;
  const isOwnContext = !!practice && !!ownPracticeId && practice._id === ownPracticeId;

  // ── per-step state ──────────────────────────────────────────────────────
  const [hourlyRate, setHourlyRate] = useState(practice?.hourly_rate ?? 100);
  const [practiceType, setPracticeType] = useState(practice?.practice_type ?? 'wahlarzt');
  const [industries, setIndustries] = useState([]);
  const [hasLogo, setHasLogo] = useState(!!practice?.qr_logo_data_url);
  const [impressum, setImpressum] = useState(practice?.impressum ?? '');
  const [avvStatus, setAvvStatus] = useState(null);
  const [avvPreview, setAvvPreview] = useState('');
  const [avvAccepted, setAvvAccepted] = useState(false);

  useEffect(() => {
    api.get('/api/industries').then((r) => setIndustries(r.data.industries || [])).catch(() => {});
    api.get('/api/avv/status').then((r) => setAvvStatus(r.data)).catch(() => {});
    api.get('/api/avv/preview?lang=de').then((r) => setAvvPreview(r.data.text || '')).catch(() => {});
  }, []);

  async function patchPractice(payload) {
    if (!isOwnContext) return null;
    // Always PATCH the admin's OWN practice, never the currently switched-into
    // branch. Pin the X-Practice-Id header to ownPracticeId so any axios
    // default header set by LocationSwitcher cannot leak into this call.
    const r = await api.patch(`/api/practices/${ownPracticeId}`, payload, {
      headers: { 'X-Practice-Id': ownPracticeId },
    });
    setPractice(r.data.practice);
    return r.data.practice;
  }

  // ── step actions ────────────────────────────────────────────────────────
  async function saveStep1AndNext() {
    setSaving(true);
    try {
      await patchPractice({ hourly_rate: Number(hourlyRate) || 0, practice_type: practiceType });
      setStep(2);
    } catch (e) { toast.error(t('wizard.errors.saveFailed')); }
    finally { setSaving(false); }
  }

  async function onLogoPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('settings.publicPage.logoMaxSize')); return; }
    setSaving(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post('/api/qr/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHasLogo(true);
      toast.success(t('settings.publicPage.logoAdded'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('settings.publicPage.uploadFailed'));
    } finally { setSaving(false); e.target.value = ''; }
  }
  async function onLogoRemove() {
    setSaving(true);
    try { await api.delete('/api/qr/logo'); setHasLogo(false); }
    catch (_) { toast.error(t('settings.publicPage.removeFailed')); }
    finally { setSaving(false); }
  }

  async function saveStep3AndNext() {
    if (!impressum.trim() || impressum.trim().length < 20) {
      toast.error(t('wizard.errors.impressumTooShort'));
      return;
    }
    setSaving(true);
    try {
      await patchPractice({ impressum: impressum.trim() });
      setStep(4);
    } catch (e) { toast.error(t('wizard.errors.saveFailed')); }
    finally { setSaving(false); }
  }

  async function signAvvAndFinish() {
    if (!avvAccepted && !(avvStatus?.signed && !avvStatus?.outdated)) return;
    setSaving(true);
    try {
      // 1. Sign AVV if not already done — AVV is signed at the OWN practice
      //    (Hauptstandort), never at a branch context.
      const needsSigning = !avvStatus?.signed || avvStatus?.outdated;
      if (needsSigning) {
        await api.post('/api/avv/sign', { accepted: true }, {
          headers: { 'X-Practice-Id': ownPracticeId },
        });
        window.dispatchEvent(new CustomEvent('studioops:avv-updated'));
      }
      // 2. Show "Done" screen FIRST (with confetti) — do this before
      //    persisting wizard_completed, otherwise the parent DashboardLayout
      //    unmounts the wizard before the user sees the success state.
      setDone(true);
      fireConfetti();
      // 3. Persist wizard_completed in the background — explicitly pin to
      //    ownPracticeId so the request can never target a branch by mistake.
      api.patch(`/api/practices/${ownPracticeId}`, {
        wizard_completed: true,
        wizard_completed_at: new Date().toISOString(),
      }, {
        headers: { 'X-Practice-Id': ownPracticeId },
      }).then((r) => {
        // Refresh practice in context AFTER navigating away so the wizard
        // doesn't disappear mid-success-state.
        setTimeout(() => setPractice(r.data.practice), 2400);
      }).catch(() => { /* user can re-complete next login if it fails */ });
      // 4. Navigate to /heute after celebration.
      setTimeout(() => navigate('/heute', { replace: true }), 2200);
    } catch (e) {
      toast.error(e.response?.data?.message || t('wizard.errors.saveFailed'));
      setSaving(false);
    }
  }

  function fireConfetti() {
    const duration = 1600;
    const end = Date.now() + duration;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors: ['#1B3A6B', '#16A34A', '#D97706', '#2B7FD4'] });
      confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors: ['#1B3A6B', '#16A34A', '#D97706', '#2B7FD4'] });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }

  function skip() {
    if (step === 1) setStep(2);
    else if (step === 2) setStep(3);
  }

  // ── render ───────────────────────────────────────────────────────────────
  // Defensive: never render against a branch context (DashboardLayout already
  // gates this; this protects standalone usage and any future direct mounts).
  if (!isOwnContext) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-navy/85 backdrop-blur-sm overflow-y-auto" data-testid="onboarding-wizard">
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Progress */}
          <div className="px-8 pt-7 pb-3" data-testid="wizard-progress">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">
                {t('wizard.stepLabel', { current: step, total: TOTAL_STEPS })}
              </p>
              <p className="text-[11px] text-text-secondary">{t('wizard.welcomeBack', { name: practice?.name || '' })}</p>
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden">
              <div
                data-testid="wizard-progress-bar"
                className="h-full bg-accent transition-all duration-500"
                style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
              />
            </div>
          </div>

          {/* Success state */}
          {done ? (
            <div className="px-8 py-14 text-center" data-testid="wizard-done">
              <div className="inline-flex w-16 h-16 rounded-full bg-success/10 text-success items-center justify-center mb-4">
                <Sparkles size={32}/>
              </div>
              <h2 className="text-2xl font-bold text-navy">{t('wizard.doneTitle')}</h2>
              <p className="mt-2 text-sm text-text-secondary">{t('wizard.doneSubtitle')}</p>
              <p className="mt-6 text-xs text-text-secondary animate-pulse">{t('wizard.redirecting')}</p>
            </div>
          ) : (
            <div className="px-8 py-7">
              {/* Step 1 — Stundensatz */}
              {step === 1 && (
                <div data-testid="wizard-step-1">
                  <div className="flex items-center gap-2 mb-1 text-accent"><Euro size={18}/><p className="text-[11px] uppercase tracking-wider font-semibold">{t('wizard.step1.tag')}</p></div>
                  <h2 className="text-2xl font-bold text-navy">{t('wizard.step1.title')}</h2>
                  <p className="mt-2 text-sm text-text-secondary">{t('wizard.step1.subtitle')}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="so-label">{t('wizard.step1.industryLabel')}</label>
                      <select value={practiceType} onChange={(e) => setPracticeType(e.target.value)} className="so-input" data-testid="wizard-practice-type">
                        {industries.map((i) => <option key={i.id} value={i.id}>{i.label_de}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="so-label">{t('wizard.step1.rateLabel')}</label>
                      <div className="relative">
                        <input type="number" min={0} step="1" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} className="so-input pr-10" data-testid="wizard-hourly-rate"/>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-sm">€/h</span>
                      </div>
                      <p className="mt-1 text-[11px] text-text-secondary">{t('wizard.step1.rateHint')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 — Logo */}
              {step === 2 && (
                <div data-testid="wizard-step-2">
                  <div className="flex items-center gap-2 mb-1 text-accent"><Upload size={18}/><p className="text-[11px] uppercase tracking-wider font-semibold">{t('wizard.step2.tag')}</p></div>
                  <h2 className="text-2xl font-bold text-navy">{t('wizard.step2.title')}</h2>
                  <p className="mt-2 text-sm text-text-secondary">{t('wizard.step2.subtitle')}</p>
                  <div className="mt-6 flex items-center gap-6 flex-wrap">
                    <div className="relative" data-testid="wizard-qr-preview">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/qr/${practice?.slug}.png?_=${hasLogo}`}
                        alt="QR"
                        className="w-36 h-36 border border-border rounded-md bg-white"
                      />
                      {hasLogo && (
                        <button onClick={onLogoRemove} disabled={saving}
                          className="absolute -top-2 -right-2 bg-white border border-border rounded-full w-7 h-7 flex items-center justify-center shadow text-text-secondary hover:text-danger"
                          aria-label={t('settings.publicPage.removeLogo')} data-testid="wizard-logo-remove">
                          <XIcon size={14}/>
                        </button>
                      )}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className={`so-btn so-btn-primary cursor-pointer ${saving ? 'opacity-50 pointer-events-none' : ''}`} data-testid="wizard-logo-upload-label">
                        <Upload size={14}/>{hasLogo ? t('settings.publicPage.replaceLogo') : t('settings.publicPage.uploadLogo')}
                        <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onLogoPick} data-testid="wizard-logo-input"/>
                      </label>
                      <p className="mt-2 text-xs text-text-secondary">{t('settings.publicPage.logoNote')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 — Impressum */}
              {step === 3 && (
                <div data-testid="wizard-step-3">
                  <div className="flex items-center gap-2 mb-1 text-accent"><FileText size={18}/><p className="text-[11px] uppercase tracking-wider font-semibold">{t('wizard.step3.tag')}</p></div>
                  <h2 className="text-2xl font-bold text-navy">{t('wizard.step3.title')}</h2>
                  <p className="mt-2 text-sm text-text-secondary">{t('wizard.step3.subtitle')}</p>
                  <textarea
                    rows={10}
                    className="so-input font-sans text-sm leading-relaxed mt-6"
                    placeholder={t('settings.legal.impressumPlaceholder')}
                    value={impressum}
                    onChange={(e) => setImpressum(e.target.value)}
                    data-testid="wizard-impressum"
                  />
                  <p className="mt-1 text-[11px] text-text-secondary">{t('wizard.step3.hint')}</p>
                </div>
              )}

              {/* Step 4 — AVV */}
              {step === 4 && (
                <div data-testid="wizard-step-4">
                  <div className="flex items-center gap-2 mb-1 text-accent"><ShieldCheck size={18}/><p className="text-[11px] uppercase tracking-wider font-semibold">{t('wizard.step4.tag')}</p></div>
                  <h2 className="text-2xl font-bold text-navy">{t('wizard.step4.title')}</h2>
                  <p className="mt-2 text-sm text-text-secondary">{t('wizard.step4.subtitle')}</p>
                  {avvStatus?.signed && !avvStatus?.outdated ? (
                    <div className="mt-6 rounded-md bg-success/10 border border-success/30 p-4 flex items-start gap-3" data-testid="wizard-avv-already-signed">
                      <Check size={18} className="text-success shrink-0 mt-0.5"/>
                      <div>
                        <p className="text-sm font-semibold text-success">{t('wizard.step4.alreadySigned')}</p>
                        <p className="text-xs text-text-secondary mt-1">Version {avvStatus.avv_version}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mt-5 max-h-72 overflow-y-auto rounded-md border border-border bg-background/40 p-4" data-testid="wizard-avv-preview">
                        <pre className="font-sans text-[12px] leading-relaxed whitespace-pre-wrap text-text">{avvPreview || '…'}</pre>
                      </div>
                      <label className="mt-4 flex items-start gap-2 text-sm cursor-pointer">
                        <input type="checkbox" className="mt-0.5" checked={avvAccepted} onChange={(e) => setAvvAccepted(e.target.checked)} data-testid="wizard-avv-accept"/>
                        <span>{t('wizard.step4.acceptLabel')}</span>
                      </label>
                    </>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="mt-8 pt-5 border-t border-border flex items-center justify-between flex-wrap gap-3" data-testid="wizard-nav">
                <button
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  disabled={step === 1 || saving}
                  className="so-btn so-btn-secondary text-sm disabled:invisible"
                  data-testid="wizard-back"
                >
                  <ArrowLeft size={14}/>{t('common.back')}
                </button>
                <div className="flex items-center gap-2">
                  {(step === 1 || step === 2) && (
                    <button
                      onClick={skip}
                      disabled={saving}
                      className="text-xs text-text-secondary hover:text-text underline"
                      data-testid="wizard-skip"
                    >
                      {t('wizard.skip')}
                    </button>
                  )}
                  {step === 1 && (
                    <button onClick={saveStep1AndNext} disabled={saving || !hourlyRate} className="so-btn so-btn-primary text-sm" data-testid="wizard-next-1">
                      {saving ? '…' : t('wizard.next')}<ArrowRight size={14}/>
                    </button>
                  )}
                  {step === 2 && (
                    <button onClick={() => setStep(3)} disabled={saving} className="so-btn so-btn-primary text-sm" data-testid="wizard-next-2">
                      {t('wizard.next')}<ArrowRight size={14}/>
                    </button>
                  )}
                  {step === 3 && (
                    <button onClick={saveStep3AndNext} disabled={saving} className="so-btn so-btn-primary text-sm" data-testid="wizard-next-3">
                      {saving ? '…' : t('wizard.next')}<ArrowRight size={14}/>
                    </button>
                  )}
                  {step === 4 && (
                    <button
                      onClick={signAvvAndFinish}
                      disabled={saving || (!avvAccepted && !(avvStatus?.signed && !avvStatus?.outdated))}
                      className="so-btn so-btn-primary text-sm"
                      data-testid="wizard-finish"
                    >
                      {saving ? '…' : t('wizard.finish')}<Sparkles size={14}/>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
